import { type TabBarConfig } from './tab-bar';

/** Скалярные значения для метаданных (совместимо с аналитикой/платежами ядра). */
export type BridgeScalar = string | number | boolean | null;

/** Тип уведомления, которое native показывает после закрытия мини-аппа. */
export type CloseNotificationType = 'success' | 'info' | 'warn' | 'error';

/**
 * Причина закрытия мини-аппа: web просит native показать уведомление,
 * чтобы пользователю было понятно, почему его «выбросило» из сервиса.
 */
export interface CloseNotification {
  type: CloseNotificationType;
  message: string;
}

/** Системные действия (web → native). Доменные действия мини-апп объявляет отдельно. */
export const SYSTEM_ACTIONS = {
  Ready: 'bridge.ready',
  NavigationNavigate: 'navigation.navigate',
  NavigationBack: 'navigation.back',
  NavigationOpenMiniApp: 'navigation.openMiniApp',
  NavigationClose: 'navigation.close',
  NavigationSetCanGoBack: 'navigation.setCanGoBack',
  AuthGetStatus: 'auth.getStatus',
  AuthGetUser: 'auth.getUser',
  AuthHasScope: 'auth.hasScope',
  SsoAuthorize: 'sso.authorize',
  SaveCity: 'city.save',
  LocationGetCurrentPosition: 'location.getCurrentPosition',
  PaymentsPay: 'payments.pay',
  AnalyticsTrack: 'analytics.track',
  UiSetTitle: 'ui.setTitle',
  UiSetTabBarVisible: 'ui.setTabBarVisible',
  UiSetTabBar: 'ui.setTabBar',
  UiOpenExternal: 'ui.openExternal',
  UiShare: 'ui.share',
  UiHaptics: 'ui.haptics',
  Log: 'log'
} as const;

export type SystemActionName =
  (typeof SYSTEM_ACTIONS)[keyof typeof SYSTEM_ACTIONS];

/** Типы payload системных действий. */
export interface SystemActionPayloads {
  'bridge.ready': { sdkVersion?: string } | null;
  'navigation.navigate': { route: string; params?: Record<string, unknown> };
  'navigation.back': null;
  'navigation.openMiniApp': {
    miniAppId: string;
    params?: Record<string, unknown>;
  };
  /**
   * Закрыть мини-апп. Опциональный `reason` — причина закрытия: native
   * показывает её пользователю (тост/баннер нужного типа). Без payload (`null`)
   * — обычное закрытие по инициативе пользователя, без уведомления.
   */
  'navigation.close': { reason?: CloseNotification } | null;
  'navigation.setCanGoBack': { value: boolean };
  'auth.getStatus': null;
  'auth.getUser': null;
  'auth.hasScope': { scope: string };
  /**
   * Запросить SSO-авторизацию по инициативе web (например, после 401 на закрытый эндпоинт
   * своего бэка или проактивно). Native при отсутствии суперапп-сессии триггерит вход
   * (код-пароль/биометрия/логин), затем возвращает свежий одноразовый код. Ответ (RPC) —
   * `{ authorizationCode, codeVerifier, expiresInSeconds }`.
   */
  'sso.authorize': null;
  /**
   * Запросить текущую геопозицию устройства (RPC). Native запрашивает разрешение и
   * возвращает сырые координаты `{ status:'ok', latitude, longitude }` либо
   * `{ status:'denied' }` / `{ status:'error' }`. Сопоставление координат с городом —
   * на стороне web (по своему каталогу), native город не определяет.
   */
  'location.getCurrentPosition': null;
  'payments.pay': {
    amount: number;
    currency: string;
    description?: string;
    metadata?: Record<string, BridgeScalar>;
  };
  'analytics.track': { event: string; params?: Record<string, BridgeScalar> };
  'ui.setTitle': { title: string };
  'ui.setTabBarVisible': { value: boolean };
  'ui.setTabBar': TabBarConfig;
  'ui.openExternal': { url: string };
  'ui.share': { message?: string; url?: string; title?: string };
  'ui.haptics': {
    type?: 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';
  };
  log: {
    level?: 'log' | 'info' | 'warn' | 'error';
    message: string;
    data?: unknown;
  };
}

const SYSTEM_ACTION_SET: ReadonlySet<string> = new Set(
  Object.values(SYSTEM_ACTIONS)
);

/** Действия, ожидающие ответа (RPC). */
const RPC_ACTION_SET: ReadonlySet<string> = new Set<string>([
  SYSTEM_ACTIONS.AuthGetStatus,
  SYSTEM_ACTIONS.AuthGetUser,
  SYSTEM_ACTIONS.AuthHasScope,
  SYSTEM_ACTIONS.SsoAuthorize,
  SYSTEM_ACTIONS.LocationGetCurrentPosition,
  SYSTEM_ACTIONS.PaymentsPay
]);

export function isSystemAction(action: string): action is SystemActionName {
  return SYSTEM_ACTION_SET.has(action);
}

export function isRpcAction(action: string): boolean {
  return RPC_ACTION_SET.has(action);
}
